/* ============================================================
   AGILITE QR IMPORT  (Step-0 recovery) — ISOLATED asset
   ------------------------------------------------------------
   Upload-a-QR-photo path for shoppers who reached
   /pages/free-patch-offer WITHOUT the ?sku= scan context
   (shared link, typed URL, desktop). They upload/snap a photo
   of the QR on the product tag; we decode it in-browser and
   reload the page with the extracted ?sku= (or ?t=) so the
   normal qr-signup flow takes over. Refresh-safe by design:
   the result lives in the URL, not in JS state.

   Decode engines, in order:
     1. Native BarcodeDetector (Chrome/Android — fast, free)
     2. jsQR fallback (vendored asset, lazy-loaded from
        window.__AGB_JSQR_URL only when actually needed —
        covers iOS Safari + desktop browsers without the API)

   SECURITY: the decoded QR text is UNTRUSTED input. We never
   navigate to the URL inside the QR — we only extract a sku/t
   param that passes a strict shape check and mint our own
   same-page relative URL from it.

   Gating: the section only appears when the page has no scan
   context. window.__AGB_JSQR_URL unset => native-only (no lib).
   ============================================================ */
(function () {
  'use strict';

  var SKU_RE   = /^[A-Z0-9][A-Z0-9-]{2,31}$/;      // canonical Agilite variant SKU shape
  var TOKEN_RE = /^[A-Za-z0-9_-]{4,512}$/;         // base64url signed-token shape
  var MAX_UPLOAD_BYTES = 12 * 1024 * 1024;
  var DECODE_DIMS = [1024, 1792];                  // downscale first; retry bigger if no hit

  // ---- scan-context check (mirrors qr-signup resolveScan, read-only) ----
  function hasScanContext() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('sku') || params.get('t')) return true;
    var qs = window.location.search.replace(/^\?/, '');
    var first = qs ? qs.split('&')[0] : '';
    return !!(first && first.indexOf('=') === -1);   // bare legacy token counts as scanned
  }

  // ---- decoded-text -> {sku} | {t} | null ------------------------------
  function extractScan(text) {
    if (!text) return null;
    text = String(text).trim();
    var qIdx = text.indexOf('?');
    if (/^(https?:)?\/\//i.test(text) || qIdx !== -1) {
      var qs = qIdx !== -1 ? text.slice(qIdx + 1).split('#')[0] : '';
      var params = new URLSearchParams(qs);
      var sku = (params.get('sku') || '').trim().toUpperCase();
      if (sku && SKU_RE.test(sku)) return { sku: sku };
      var t = (params.get('t') || '').trim();
      if (t && TOKEN_RE.test(t)) return { t: t };
      return null;
    }
    var bare = text.toUpperCase();
    if (SKU_RE.test(bare)) return { sku: bare };     // SKU printed directly into the QR
    return null;
  }

  function go(scan) {
    var q = scan.sku ? 'sku=' + encodeURIComponent(scan.sku)
                     : 't='   + encodeURIComponent(scan.t);
    window.location.assign(window.location.pathname + '?' + q);
  }

  // ---- image decode ----------------------------------------------------
  function toBitmap(blob) {
    if (window.createImageBitmap) {
      try {
        return window.createImageBitmap(blob, { imageOrientation: 'from-image' })
          .catch(function () { return window.createImageBitmap(blob); });
      } catch (e) { /* fall through */ }
    }
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode_failed')); };
      img.src = url;
    });
  }

  function nativeDetect(bmp) {
    if (!('BarcodeDetector' in window)) return Promise.resolve(null);
    try {
      return window.BarcodeDetector.getSupportedFormats()
        .then(function (fmts) {
          if (!fmts || fmts.indexOf('qr_code') === -1) return null;
          var det = new window.BarcodeDetector({ formats: ['qr_code'] });
          return det.detect(bmp).then(function (codes) {
            return (codes && codes.length && codes[0].rawValue) || null;
          });
        })
        .catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  var _jsqrLoad = null;
  function loadJsqr() {
    if (window.jsQR) return Promise.resolve(window.jsQR);
    if (!_jsqrLoad) {
      _jsqrLoad = new Promise(function (resolve, reject) {
        var url = window.__AGB_JSQR_URL;
        if (!url) return reject(new Error('jsqr_unavailable'));
        var s = document.createElement('script');
        s.src = url; s.async = true;
        s.onload  = function () { window.jsQR ? resolve(window.jsQR) : reject(new Error('jsqr_missing')); };
        s.onerror = function () { reject(new Error('jsqr_load_failed')); };
        document.head.appendChild(s);
      });
    }
    return _jsqrLoad;
  }

  function jsqrDetect(bmp) {
    return loadJsqr().then(function (jsQR) {
      var w = bmp.width || bmp.naturalWidth, h = bmp.height || bmp.naturalHeight;
      for (var i = 0; i < DECODE_DIMS.length; i++) {
        var scale = Math.min(1, DECODE_DIMS[i] / Math.max(w, h));
        var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
        var canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(bmp, 0, 0, cw, ch);
        var data = ctx.getImageData(0, 0, cw, ch);
        var hit = jsQR(data.data, cw, ch, { inversionAttempts: 'attemptBoth' });
        if (hit && hit.data) return hit.data;
        if (scale === 1) break;                       // bigger retry would be identical
      }
      return null;
    }).catch(function () { return null; });
  }

  function decodeBlob(blob) {
    return toBitmap(blob).then(function (bmp) {
      return nativeDetect(bmp).then(function (text) {
        if (text) return { text: text, engine: 'native' };
        return jsqrDetect(bmp).then(function (t2) {
          if (bmp.close) { try { bmp.close(); } catch (e) {} }
          return t2 ? { text: t2, engine: 'jsqr' } : null;
        });
      });
    });
  }

  // ---- UI --------------------------------------------------------------
  function setStatus(msg, isErr) {
    var el = document.getElementById('qi-status');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
    el.classList.toggle('err', !!isErr);
  }

  function onFile(file) {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) { setStatus('That image is too large — try again.', true); return; }
    if (!/^image\//.test(file.type))  { setStatus('Please choose a photo.', true); return; }
    var btn = document.getElementById('qi-btn');
    if (btn) btn.disabled = true;
    setStatus('Reading the code…', false);
    decodeBlob(file).then(function (res) {
      var scan = res && extractScan(res.text);
      if (scan) {
        setStatus('Code found — loading your item…', false);
        go(scan);
        return;
      }
      if (btn) btn.disabled = false;
      setStatus(res
        ? 'That code isn’t an Agilite product tag.'
        : 'No QR code found in that photo — try a closer, sharper shot.', true);
    }).catch(function () {
      if (btn) btn.disabled = false;
      setStatus('Could not read that photo — try another.', true);
    });
  }

  function init() {
    var section = document.getElementById('qr-import');
    if (!section || hasScanContext()) return;
    section.hidden = false;
    var input = document.getElementById('qi-file');
    var btn   = document.getElementById('qi-btn');
    if (btn && input) {
      btn.addEventListener('click', function () { input.click(); });
      input.addEventListener('change', function () { onFile(input.files && input.files[0]); });
    }
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // handleBlob: programmatic entry for dev/testing (same pipeline as the picker)
  window.AGBQrImport = { handleBlob: onFile, extractScan: extractScan };
})();
